import { useState, useEffect } from "react";
import { Calendar, Bell, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

// API URL
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

function CountdownTimer({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const target = targetDate ? new Date(targetDate + "T00:00:00") : new Date("2026-02-28T00:00:00");
    const difference = target - new Date();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const timeUnits = [
    { value: timeLeft.days, label: "Jours" },
    { value: timeLeft.hours, label: "Heures" },
    { value: timeLeft.minutes, label: "Minutes" },
    { value: timeLeft.seconds, label: "Secondes" },
  ];

  return (
    <div className="flex justify-center gap-3 sm:gap-4">
      {timeUnits.map((unit, index) => (
        <div key={unit.label} className="flex flex-col items-center">
          <div className="countdown-item w-16 sm:w-20 h-16 sm:h-20 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="font-heading text-2xl sm:text-3xl font-bold text-white">
              {String(unit.value).padStart(2, "0")}
            </span>
          </div>
          <span className="font-body text-xs sm:text-sm text-forest/70 mt-2">
            {unit.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function WaitlistForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferences: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mode statique - redirection vers email
    const subject = encodeURIComponent("Inscription liste d'attente - La Taïga Siberia");
    const body = encodeURIComponent(`Nom: ${formData.name}\nEmail: ${formData.email}\nTéléphone: ${formData.phone || 'Non renseigné'}\n\nPréférences:\n${formData.preferences || 'Aucune préférence spécifiée'}`);
    
    window.location.href = `mailto:lataigasiberia@gmail.com?subject=${subject}&body=${body}`;
    
    toast.success("Redirection vers votre messagerie pour finaliser l'inscription...");
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="waitlist-name" className="font-body text-sm font-semibold text-forest">
            Nom complet *
          </Label>
          <Input
            id="waitlist-name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 bg-white border-sand focus:border-forest focus:ring-forest"
            placeholder="Votre nom"
            data-testid="waitlist-name-input"
          />
        </div>
        <div>
          <Label htmlFor="waitlist-email" className="font-body text-sm font-semibold text-forest">
            Email *
          </Label>
          <Input
            id="waitlist-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1 bg-white border-sand focus:border-forest focus:ring-forest"
            placeholder="votre@email.com"
            data-testid="waitlist-email-input"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="waitlist-phone" className="font-body text-sm font-semibold text-forest">
          Téléphone
        </Label>
        <Input
          id="waitlist-phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="mt-1 bg-white border-sand focus:border-forest focus:ring-forest"
          placeholder="06 XX XX XX XX"
          data-testid="waitlist-phone-input"
        />
      </div>
      <div>
        <Label htmlFor="waitlist-preferences" className="font-body text-sm font-semibold text-forest">
          Vos préférences (sexe, couleur...)
        </Label>
        <Textarea
          id="waitlist-preferences"
          value={formData.preferences}
          onChange={(e) => setFormData({ ...formData, preferences: e.target.value })}
          className="mt-1 bg-white border-sand focus:border-forest focus:ring-forest resize-none"
          placeholder="Décrivez vos préférences si vous en avez..."
          rows={3}
          data-testid="waitlist-preferences-input"
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-autumn hover:bg-autumn-dark text-white font-body font-semibold py-6 rounded-full transition-all duration-300"
        data-testid="waitlist-submit-btn"
      >
        <Bell className="w-5 h-5 mr-2" />
        {isSubmitting ? "Inscription en cours..." : "M'inscrire sur la liste d'attente"}
      </Button>
    </form>
  );
}

export default function LittersSection() {
  const [settings, setSettings] = useState({
    next_litter_date: "2026-02-28",
    next_litter_parents: "Sia × Caramel",
    next_litter_description: "Chatons Brown Blotched et/ou Sunshine Blotched attendus",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const formattedDate = settings.next_litter_date
    ? new Date(settings.next_litter_date + "T00:00:00").toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : "fin février 2026";

  return (
    <section
      id="portees"
      data-testid="litters-section"
      className="relative py-24 lg:py-32 bg-cream overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 left-20 text-8xl">&#x1f431;</div>
        <div className="absolute bottom-20 right-20 text-8xl">&#x1f431;</div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-12">
          <span className="font-accent text-autumn text-xl">Événement à venir</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Futures Portées
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Notre prochaine portée est prévue pour {formattedDate}.
            Inscrivez-vous dès maintenant pour être informé de la naissance des chatons.
          </p>
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Countdown & Info */}
          <div className="section-animate space-y-8">
            <div className="bg-white rounded-3xl p-8 shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-forest" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-forest">
                    Prochaine portée
                  </h3>
                  <p className="font-body text-sm text-forest/60">
                    {formattedDate}
                  </p>
                </div>
              </div>

              <CountdownTimer targetDate={settings.next_litter_date} />

              <div className="mt-8 pt-6 border-t border-sand/50">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-autumn mt-1" />
                  <div>
                    <p className="font-body font-medium text-forest">
                      Parents : {settings.next_litter_parents}
                    </p>
                    {settings.next_litter_description && (
                      <p className="font-body text-sm text-forest/70 mt-1">
                        {settings.next_litter_description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-sand/30 rounded-2xl p-6">
              <h4 className="font-heading text-lg font-semibold text-forest mb-3">
                Comment ça marche ?
              </h4>
              <ul className="font-body text-sm text-forest/80 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-autumn font-bold">1.</span>
                  Inscrivez-vous sur notre liste d'attente
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autumn font-bold">2.</span>
                  Vous serez informé dès la confirmation de gestation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autumn font-bold">3.</span>
                  Les visites débutent à partir de la 6ème semaine
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-autumn font-bold">4.</span>
                  Réservation par ordre d'inscription
                </li>
              </ul>
            </div>
          </div>

          {/* Right - Form */}
          <div className="section-animate">
            <div className="bg-white rounded-3xl p-8 shadow-soft">
              <h3 className="font-heading text-2xl font-semibold text-forest mb-2">
                Liste d'attente
              </h3>
              <p className="font-body text-forest/70 mb-6">
                Remplissez ce formulaire pour être contacté en priorité
                lors de l'annonce de notre prochaine portée.
              </p>

              <WaitlistForm />

              <p className="font-body text-xs text-forest/50 mt-4 text-center">
                Vos données ne seront utilisées que pour vous contacter
                concernant nos portées.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
